/**
 * 자수기 실 색 팔레트 (색 번호 → RGB). PES/PEC 와 JEF 는 파일에 색을 팔레트 번호로 적는다.
 * 값은 pyembroidery 1.5.1 (MIT, Copyright (c) 2018, 작성자 Tatarize) 의 EmbThreadPec.py / EmbThreadJef.py 에서 옮겼다. NOTICE.md 참고.
 * 0번은 비어 있다(null).
 */
export type PaletteEntry = [number, string] | null;

export const PEC_PALETTE: PaletteEntry[] = [
  null,
  [0x0e1f7c, "Prussian Blue"],
  [0x0a55a3, "Blue"],
  [0x008777, "Teal Green"],
  [0x4b6baf, "Cornflower Blue"],
  [0xed171f, "Red"],
  [0xd15c00, "Reddish Brown"],
  [0x913697, "Magenta"],
  [0xe49acb, "Light Lilac"],
  [0x915fac, "Lilac"],
  [0x9ed67d, "Mint Green"],
  [0xe8a900, "Deep Gold"],
  [0xfeba35, "Orange"],
  [0xffff00, "Yellow"],
  [0x70bc1f, "Lime Green"],
  [0xba9800, "Brass"],
  [0xa8a8a8, "Silver"],
  [0x7d6f00, "Russet Brown"],
  [0xffffb3, "Cream Brown"],
  [0x4f5556, "Pewter"],
  [0x000000, "Black"],
  [0x0b3d91, "Ultramarine"],
  [0x770176, "Royal Purple"],
  [0x293133, "Dark Gray"],
  [0x2a1301, "Dark Brown"],
  [0xf64a8a, "Deep Rose"],
  [0xb27624, "Light Brown"],
  [0xfcbbc5, "Salmon Pink"],
  [0xfe370f, "Vermilion"],
  [0xf0f0f0, "White"],
  [0x6a1c8a, "Violet"],
  [0xa8ddc4, "Seacrest"],
  [0x2584bb, "Sky Blue"],
  [0xfeb343, "Pumpkin"],
  [0xfff36b, "Cream Yellow"],
  [0xd0a660, "Khaki"],
  [0xd15400, "Clay Brown"],
  [0x66ba49, "Leaf Green"],
  [0x134a46, "Peacock Blue"],
  [0x878787, "Gray"],
  [0xd8ccc6, "Warm Gray"],
  [0x435607, "Dark Olive"],
  [0xfdd9de, "Flesh Pink"],
  [0xf993bc, "Pink"],
  [0x003822, "Deep Green"],
  [0xb2afd4, "Lavender"],
  [0x686ab0, "Wisteria Violet"],
  [0xefe3b9, "Beige"],
  [0xf73866, "Carmine"],
  [0xb54b64, "Amber Red"],
  [0x132b1a, "Olive Green"],
  [0xc70156, "Dark Fuchsia"],
  [0xfe9e32, "Tangerine"],
  [0xa8deeb, "Light Blue"],
  [0x00673e, "Emerald Green"],
  [0x4e2990, "Purple"],
  [0x2f7e20, "Moss Green"],
  [0xffcccc, "Flesh Pink"],
  [0xffd911, "Harvest Gold"],
  [0x095ba6, "Electric Blue"],
  [0xf0f970, "Lemon Yellow"],
  [0xe3f35b, "Fresh Green"],
  [0xff9900, "Orange"],
  [0xfff08d, "Cream Yellow"],
  [0xffc8c8, "Applique"],
];

export const JEF_PALETTE: PaletteEntry[] = [
  null,
  [0x000000, "Black"],
  [0xffffff, "White"],
  [0xffff17, "Yellow"],
  [0xff6600, "Orange"],
  [0x2f5933, "Olive Green"],
  [0x237336, "Green"],
  [0x65c2c8, "Sky"],
  [0xab5a96, "Purple"],
  [0xf669a0, "Pink"],
  [0xff0000, "Red"],
  [0xb1704e, "Brown"],
  [0x0b2f84, "Blue"],
  [0xe4c35d, "Gold"],
  [0x481a05, "Dark Brown"],
  [0xac9cc7, "Pale Violet"],
  [0xfcf294, "Pale Yellow"],
  [0xf999b7, "Pale Pink"],
  [0xfab381, "Peach"],
  [0xc9a480, "Beige"],
  [0x970533, "Wine Red"],
  [0xa0b8cc, "Pale Sky"],
  [0x7fc21c, "Yellow Green"],
  [0xe5e5e5, "Silver Gray"],
  [0x889b9b, "Gray"],
  [0x98d6bd, "Pale Aqua"],
  [0xb2e1e3, "Baby Blue"],
  [0x368ba0, "Powder Blue"],
  [0x4f83ab, "Bright Blue"],
  [0x386a91, "Slate Blue"],
  [0x071650, "Navy Blue"],
  [0xf999a2, "Salmon Pink"],
  [0xf9676b, "Coral"],
  [0xe3311f, "Burnt Orange"],
  [0xe2a188, "Cinnamon"],
  [0xb59474, "Umber"],
  [0xe4cf99, "Blond"],
  [0xffcb00, "Sunflower"],
  [0xe1add4, "Orchid Pink"],
  [0xc3007e, "Peony Purple"],
  [0x80004b, "Burgundy"],
  [0x540571, "Royal Purple"],
  [0xb10525, "Cardinal Red"],
  [0xcae0c0, "Opal Green"],
  [0x899856, "Moss Green"],
  [0x5c941a, "Meadow Green"],
  [0x003114, "Dark Green"],
  [0x5dae94, "Aquamarine"],
  [0x4cbf8f, "Emerald Green"],
  [0x007772, "Peacock Green"],
  [0x595b61, "Dark Gray"],
  [0xfffff2, "Ivory White"],
  [0xb15818, "Hazel"],
  [0xcb8a07, "Toast"],
  [0x986c80, "Salmon"],
  [0x98692d, "Cocoa Brown"],
  [0x4d3419, "Sienna"],
  [0x4c330b, "Sepia"],
  [0x33200a, "Dark Sepia"],
  [0x523a97, "Violet Blue"],
  [0x0d217e, "Blue Ink"],
  [0x1e77ac, "Sola Blue"],
  [0xb2dd53, "Green Dust"],
  [0xf33689, "Crimson"],
  [0xde649e, "Floral Pink"],
  [0x984161, "Wine"],
  [0x4c5612, "Olive Drab"],
  [0x4c881f, "Meadow"],
  [0xe4de79, "Mustard"],
  [0xcb8a1a, "Yellow Ocher"],
  [0xcba21c, "Old Gold"],
  [0xff9805, "Honey Dew"],
  [0xfcb257, "Tangerine"],
  [0xffe505, "Canary Yellow"],
  [0xf0331f, "Vermilion"],
  [0x1a842d, "Bright Green"],
  [0x386cae, "Ocean Blue"],
  [0xe3c4b4, "Beige Gray"],
  [0xe3ac81, "Bamboo"],
];

/** pyembroidery 와 같은 'red mean' 색 거리 (https://www.compuphase.com/cmetric.htm) */
export function colorDistance(a: number, b: number): number {
  const r1 = (a >> 16) & 255,
    g1 = (a >> 8) & 255,
    b1 = a & 255;
  const r2 = (b >> 16) & 255,
    g2 = (b >> 8) & 255,
    b2 = b & 255;
  const rm = Math.round((r1 + r2) / 2);
  const r = r1 - r2,
    g = g1 - g2,
    bl = b1 - b2;
  return (((512 + rm) * r * r) >> 8) + 4 * g * g + (((767 - rm) * bl * bl) >> 8);
}

export function nearestIndex(color: number, palette: PaletteEntry[]): number {
  let best = -1,
    bd = Infinity;
  palette.forEach((e, i) => {
    if (!e) return;
    const d = colorDistance(color, e[0]);
    if (d <= bd) {
      bd = d;
      best = i;
    }
  });
  return best;
}

export function hexToInt(hex: string): number {
  const h = hex.replace("#", "").trim();
  const v = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.slice(0, 6);
  const n = parseInt(v, 16);
  return Number.isFinite(n) ? n : 0;
}
